import { Size } from '@ps/web-core-enums';
import { usePrevious, useScreenSize } from '@ps/web-shared-components';
import classNames from 'clsx';
import type { SyntheticEvent } from 'react';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { select } from '@swf/core/store';

import styles from './LocalesDropDown.scss';
import LocalesLink from './LocalesLink/LocalesLink';
import LocalesMenu from './LocalesMenu/LocalesMenu';
import LocalesPopper from './LocalesPopper/LocalesPopper';

export interface Props {
  className?: string;
}

const LocalesDropDown: React.FC<Props> = ({ className }) => {
  const locales = useSelector(select.cms.getWebsiteLanguages);
  const [menuVisible, setMenuVisible] = useState(false);
  const isMobile = useSelector(select.device.isMobile);
  const { greaterThan, screenSize } = useScreenSize();
  const previousScreenSize = usePrevious(screenSize);

  const handleClick = useCallback(
    (e: SyntheticEvent) => {
      e.stopPropagation();
      setMenuVisible(!menuVisible);
    },
    [menuVisible]
  );

  useEffect(() => {
    const clickHandler = () => menuVisible && setMenuVisible(false);
    window.addEventListener('click', clickHandler);
    return () => window.removeEventListener('click', clickHandler);
  }, [menuVisible]);

  useEffect(() => {
    if (previousScreenSize !== screenSize) setMenuVisible(false);
  }, [previousScreenSize, screenSize]);

  if (locales.length < 2) return null;

  return !greaterThan(Size.Small) || isMobile ? (
    <>
      <LocalesLink
        className={classNames(className, styles.selector, menuVisible && styles.open)}
        menuVisible={menuVisible}
        onClick={handleClick}
      />
      {menuVisible && <LocalesMenu className={className} />}
    </>
  ) : (
    <LocalesPopper className={className} menuVisible={menuVisible} onClick={handleClick} />
  );
};

export default LocalesDropDown;
